import {
	Component,
	EventEmitter,
	Input,
	Output,
	ViewChild
}                                   from '@angular/core';
import { BaseComponent }            from '@app/core/bases/base-component/base.component';
import { FilterComponent }          from '@app/shared/list-operations/components/filter/filter.component';
import { PaginationComponent }      from '@app/shared/list-operations/components/pagination/pagination.component';
import { SearchComponent }          from '@app/shared/list-operations/components/search/search.component';
import { SortComponent }            from '@app/shared/list-operations/components/sort/sort.component';
import {
	ISortData,
	SortOrderEnum
}                                   from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { LIST_OPERATIONS_METADATA } from '@shared/list-operations/config/list-operations.config';
import {
	FilterDatepickerType,
	FilterDateRangePickerType,
	FilterIdentifiersEnum,
	FilterListType,
	FilterOptionType,
	FilterSwitchType,
	FilterTextType,
	FilterTypes,
	FilterValueType,
	IActiveFilterData,
	IActiveFilters,
	IFilter
}                                   from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	IRestoreListOperationsData,
	ListOperations,
	ListOperationsEnum,
	ListOperationsRequestStateEnum
}                                   from '@shared/list-operations/model/list-operations.model';
import {
	IPaging,
	IPagingData
}                                   from '@shared/list-operations/model/paging.model';
import { ISearchData }              from '@shared/list-operations/model/search.model';
import { ObjectUtils }              from '@utils/helpers';
import { Observable }               from 'rxjs';


@Component( {
	selector: 'app-list-operations',
	templateUrl: './list-operations.component.html',
	styleUrls: [ './list-operations.component.scss' ]
} )

export class ListOperationsComponent extends BaseComponent {

	private search: string[];

	private sort: string;

	private activeFilters: IActiveFilters;

	private checkedInitializationPreCondition: boolean;

	private searchIsInitialized: boolean;

	private sortIsInitialized: boolean;

	private filterIsInitialized: boolean;

	private pagingIsInitialized: boolean;

	private previousListOperationsData: ListOperations;
	private listOperationsData: ListOperations;

	private _listOperationsConfigData: IListOperationsConfig;

	private prevPageNumber: number;

	private _renderSearch = true;

	private _renderSort = true;

	private _paging: IPaging;

	// render
	private _renderListOperations = true;


	private _renderFilter = true;

	private _renderPagination = true;

	// hide
	private _hideSearch = false;

	private _hideSort = false;

	private _hideFilter = false;

	private _hidePagination = false;

	blockOperationEmit = false;


	@Input() set listRequest( listRequestAborted: { state: ListOperationsRequestStateEnum } ) {
		switch ( listRequestAborted?.state ) {
			case ListOperationsRequestStateEnum.ABORTED:
				this.blockOperationEmit = true;
				this.resetListOperations(
					[
						ListOperationsEnum.FILTER,
						ListOperationsEnum.PAGINATION,
						ListOperationsEnum.SEARCH,
						ListOperationsEnum.SORT
					]
				);
				this.restoreListOperationsData( this.previousListOperationsData.restoreListOperationsData );
				break;

			case ListOperationsRequestStateEnum.FINISHED:
				this.previousListOperationsData = ObjectUtils.deepCopy( this.listOperationsData );
				break;
		}
	}

	get listOperationsConfigData() {
		return this._listOperationsConfigData;
	}

	@Input( 'listOperationsConfigData' )
	set listOperationsConfigData( listOperationsConfigData: IListOperationsConfig ) {
		if ( !listOperationsConfigData ) { return; }

		this._listOperationsConfigData = ObjectUtils.deepCopy<IListOperationsConfig>( listOperationsConfigData );

		if ( listOperationsConfigData?.restoreListOperationsData?.operatorsData && listOperationsConfigData?.restoreListOperationsData?.shouldRestoreData ) {
			this.restoreListOperationsData( { ...listOperationsConfigData?.restoreListOperationsData } );
		} else {

			if ( this.renderSearch && Object.keys( listOperationsConfigData?.searchData ?? {} ).length ) {
				this.searchData = listOperationsConfigData?.searchData;
			} else {
				this.searchIsInitialized = true;
			}

			if ( this.renderSort && listOperationsConfigData?.sortData?.length ) {
				this.sortData = listOperationsConfigData?.sortData;
			} else {
				this.sortIsInitialized = true;
			}

			if ( this.renderFilter && listOperationsConfigData?.filterData?.length ) {
				this.filterData = listOperationsConfigData?.filterData.map( ( filter: IFilter, filterIndex: number ) => {
					return { ...filter, id: filterIndex };
				} );
			} else {
				this.filterIsInitialized = true;
			}

			if ( this.renderPagination && Object.keys( listOperationsConfigData?.pagingData ?? {} ).length ) {
				this.pagingData = listOperationsConfigData?.pagingData;
			} else {
				this.pagingIsInitialized = true;
			}
		}

		this._listOperationsConfigData = {
			...this._listOperationsConfigData,
			filterData: this.filterData
		};

		if ( listOperationsConfigData.reinitializeAndEmitListOperations ) {
			this.reinitializeListOps();
		}
	}

	get renderSearch(): boolean {
		return this._renderSearch;
	}

	@Input() set renderSearch( renderSearch: boolean ) {
		this._renderSearch = renderSearch ?? this._renderSearch;
	}

	activeListOperation: ListOperationsEnum;

	fallbackPaging: IPaging = {
		pageNumber: LIST_OPERATIONS_METADATA?.default?.paging?.pageNumber ?? 0,
		pageSize: LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20
	};


	// list-operators comp ###################################
	@Input() hideListOperations = false;

	@Output() operationEmitter: EventEmitter<ListOperations> = new EventEmitter();

	// operators comp ###################################
	@Input() restoreData$: Observable<ListOperations>;

	@Input() listOperationsRestoreData: ListOperations;

	searchData: ISearchData;

	sortData: ISortData[];

	filterData: IFilter[];

	pagingData: IPagingData;

	@Input() set paging( paging: IPaging ) {
		this._paging = paging;
	}

	get paging(): IPaging {
		return this._paging;
	}

	get renderListOperations(): boolean {
		return this._renderListOperations && !!this._listOperationsConfigData;
	}

	@Input() set renderListOperations( renderListOperations: boolean ) {
		this._renderListOperations = renderListOperations;
	}

	get renderSort(): boolean {
		return this._renderSort;
	}

	@Input() set renderSort( renderSort: boolean ) {
		this._renderSort = renderSort ?? this._renderSort;
	}

	get renderFilter(): boolean {
		return this._renderFilter;
	}

	@Input() set renderFilter( renderFilter: boolean ) {
		this._renderFilter = renderFilter ?? this._renderFilter;
	}

	get renderPagination(): boolean {
		return this._renderPagination;
	}

	@Input() set renderPagination( renderPagination: boolean ) {
		this._renderPagination = renderPagination ?? this._renderPagination;
	}

	get hideSearch(): boolean {
		return this._hideSearch;
	}

	@Input() set hideSearch( hideSearch: boolean ) {
		this._hideSearch = hideSearch;
	}

	get hideSort(): boolean {
		return this._hideSort;
	}

	@Input() set hideSort( hideSort: boolean ) {
		this._hideSort = hideSort;
	}

	get hideFilter(): boolean {
		return this._hideFilter;
	}

	@Input() set hideFilter( hideFilter: boolean ) {
		this._hideFilter = hideFilter;
	}

	get hidePagination(): boolean {
		return this._hidePagination;
	}

	@Input() set hidePagination( hidePagination: boolean ) {
		this._hidePagination = hidePagination;
	}

	// operators instances
	@ViewChild( 'searchCmp' ) searchCmp: SearchComponent;

	@ViewChild( 'sortCmp' ) sortCmp: SortComponent;

	@ViewChild( 'filterCmp' ) filterCmp: FilterComponent;

	@ViewChild( 'paginationCmp' ) paginationCmp: PaginationComponent;


	constructor() {
		super();
	}

	private reinitializeListOps(): void {
		this.resetListOperations(
			[
				ListOperationsEnum.FILTER,
				ListOperationsEnum.PAGINATION,
				ListOperationsEnum.SEARCH,
				ListOperationsEnum.SORT
			],
			true
		);
		this.blockOperationEmit = false;
	}

	private restoreListOperationsData( restoreListOperationsData: IRestoreListOperationsData ): void {
		const operatorsRestoreData = restoreListOperationsData?.operatorsData;

		// SEARCH
		if ( this._listOperationsConfigData?.searchData ) {
			this.searchData = {
				...this._listOperationsConfigData.searchData
			};
			if ( operatorsRestoreData?.search ) {
				this.searchData = {
					...this.searchData,
					values: operatorsRestoreData?.search
				};
			}
		}

		// SORT
		if ( this._listOperationsConfigData?.sortData?.length && operatorsRestoreData?.sort ) {
			const [ sortField, sortOrder ] = operatorsRestoreData?.sort.split( ',' );
			const sortData = [];
			this._listOperationsConfigData?.sortData?.forEach( ( sortItem: ISortData ) => {
				if ( sortItem.name === sortField ) {
					sortItem = Object.assign( {}, { ...sortItem, defaultActive: true } );
					if ( sortOrder ) {
						sortItem = Object.assign( {}, { ...sortItem, sortOrder: sortOrder as SortOrderEnum } );
					}
				} else {
					delete sortItem?.defaultActive;
					delete sortItem?.sortOrder;
				}

				sortData.push( sortItem );
			} );
			this.sortData = [ ...sortData ];
		}

		// FILTER
		if ( this._listOperationsConfigData?.filterData?.length && operatorsRestoreData?.activeFilters ) {
			const filterData: IFilter[] = [];

			this._listOperationsConfigData?.filterData.forEach( ( filterItem: IFilter, filterIndex: number ) => {
				const filterEntries = Object.entries( operatorsRestoreData?.activeFilters );
				const [ filterIdentifier, activeFilterData ]: [ FilterIdentifiersEnum, IActiveFilterData ] =
					(
						filterEntries.find( ( [ filterIdKey, activeFilterDataValue ]: [ FilterIdentifiersEnum, IActiveFilterData ] ) =>
							filterItem.filterId === filterIdKey
						) ?? []
					) as [ FilterIdentifiersEnum, IActiveFilterData ];

				const filterValues = activeFilterData?.restoreValue
														 ? activeFilterData?.restoreValue
														 : activeFilterData?.param;


				let filterItemValues: FilterValueType[];
				let filterItemSelectFilterSearch: string;


				if ( filterIdentifier ) {
					switch ( filterItem.type ) {

						case FilterTypes.FILTER_DATE_PICKER_TYPE:
							filterItemValues = [ filterValues as FilterDatepickerType[] ];
							break;

						// TODO extend with other FilterTypes on need
						case FilterTypes.FILTER_DATE_RANGE_PICKER_TYPE:
							filterItemValues = [ filterValues as FilterDateRangePickerType ];
							break;

						case FilterTypes.FILTER_LIST_TYPE:
							filterItemValues = filterValues as FilterListType[];
							break;

						case FilterTypes.FILTER_OPTION_TYPE:
							filterItemValues = filterValues as FilterOptionType[];
							break;

						case FilterTypes.FILTER_SELECT_ARCHIVING_ERS_TYPE:
						case FilterTypes.FILTER_SELECT_ARCHIVING_KEYWORD_TYPE:
						case FilterTypes.FILTER_SELECT_ARCHIVING_TSS_TYPE:
						case FilterTypes.FILTER_SELECT_BUSINESS_PREMISES_TYPE:
						case FilterTypes.FILTER_SELECT_CONTRACT_TYPE:
						case FilterTypes.FILTER_SELECT_ERS_TYPE:
						case FilterTypes.FILTER_SELECT_ORGANIZATION_TYPE:
						case FilterTypes.FILTER_SELECT_STATIC_ITEMS_TYPE:
						case FilterTypes.FILTER_SELECT_TENANT_TYPE:
						case FilterTypes.FILTER_SELECT_TSS_TYPE:
							filterItemSelectFilterSearch = filterValues as string;
							break;

						case FilterTypes.FILTER_SWITCH_TYPE:
							filterItemValues = [ filterValues as FilterSwitchType[] ];
							break;

						case FilterTypes.FILTER_TEXT_TYPE:
							filterItemValues = [ filterValues as FilterTextType ];
							break;
					}
				}

				filterData.push(
					{
						...filterItem,
						id: filterIndex,
						values: filterItemValues,
						selectFilter: {
							...filterItem.selectFilter,
							search: filterItemSelectFilterSearch
						}
					} );
			} );
			this.filterData = [ ...filterData ];
		}

		// PAGING
		if ( this._listOperationsConfigData?.pagingData ) {
			this.pagingData = {
				...this._listOperationsConfigData.pagingData
			};
			if ( operatorsRestoreData?.paging ) {
				this.pagingData = {
					...this.pagingData,
					defaultValues: {
						pageNumber: operatorsRestoreData?.paging?.pageNumber,
						pageSize: this._listOperationsConfigData.pagingData?.defaultValues?.pageSize
					}
				};
			}
		}
	}

	private resetListOperationsData() {

		this.filterIsInitialized = false;
		this.searchIsInitialized = false;
		this.sortIsInitialized = false;
		this.pagingIsInitialized = false;
		this.blockOperationEmit = true;

		const defaultPaging = { ...this.pagingData?.defaultValues } ?? this.fallbackPaging;

		this.listOperationsData = {
			id: this.listOperationsData?.id,
			sort: null,
			search: [],
			activeFilters: {},
			paging: defaultPaging
		};

		this.prevPageNumber = 0;
	}

	private onListOperatorInitializationFinish(): void {

		// set as initialized when render is false or no configData exists
		if ( !this.checkedInitializationPreCondition ) {
			this.checkedInitializationPreCondition = true;
			this.searchIsInitialized = this.searchIsInitialized || !this.renderSearch || !this._listOperationsConfigData?.searchData;
			this.sortIsInitialized = this.sortIsInitialized || !this.renderSort || !this._listOperationsConfigData?.sortData;
			this.filterIsInitialized = this.filterIsInitialized || !this.renderFilter || !this._listOperationsConfigData?.filterData;
			this.pagingIsInitialized = this.pagingIsInitialized || !this.renderPagination || !this._listOperationsConfigData?.pagingData;
		}


		if ( this.filterIsInitialized && this.searchIsInitialized && this.sortIsInitialized && this.pagingIsInitialized && !this.blockOperationEmit ) {
			this.blockOperationEmit = false;
			this.onOperationEmitter();
		}
	}

	private onOperationEmitter() {
		const search = this.search;
		const sort = this.sort;
		const filters = this.activeFilters;
		const newPageNumber = this.paging?.pageNumber;

		// check for existing list operations
		let activeListOperationExist = false;

		if ( this.prevPageNumber !== newPageNumber ) {
			activeListOperationExist = true;
			this.prevPageNumber = newPageNumber;
		}
		if ( !activeListOperationExist && search && search.length > 0 ) { activeListOperationExist = true; }
		if ( !activeListOperationExist && sort && sort.length > 0 ) { activeListOperationExist = true; }
		if ( !activeListOperationExist && filters && Object.keys( filters ).length > 0 ) { activeListOperationExist = true; }

		if ( !activeListOperationExist ) {
			this.resetListOperationsData();
		}

		this.listOperationsData = {
			id: this._listOperationsConfigData?.id,
			configData: this._listOperationsConfigData,
			search: this.search,
			sort: this.sort,
			activeFilters: this.activeFilters,
			paging: this.paging,
			restoreListOperationsData:
				activeListOperationExist
				? {
						operatorsData: {
							search: this.search,
							sort: this.sort,
							activeFilters: this.activeFilters
														 ? { ...this.activeFilters }
														 : {},
							paging: this.paging
											? { ...this.paging }
											: null
						},
						shouldRestoreData: false
					}
				: null
		};

		this.operationEmitter.emit( this.listOperationsData );
	}

	resetListOperations( operatorList: ListOperationsEnum[], toDefaultValues?: boolean ): void {

		if ( this.searchCmp && operatorList.includes( ListOperationsEnum.SEARCH ) ) { this.searchCmp.resetListOperator(); }
		if ( this.sortCmp && operatorList.includes( ListOperationsEnum.SORT ) ) { this.sortCmp.resetListOperator(); }
		if ( this.filterCmp && operatorList.includes( ListOperationsEnum.FILTER ) ) { this.filterCmp.resetListOperator( toDefaultValues ); }
		if ( this.paginationCmp && operatorList.includes( ListOperationsEnum.PAGINATION ) ) { this.paginationCmp.resetListOperator(); }

		this.resetListOperationsData();

		if ( !this.blockOperationEmit ) {
			this.onOperationEmitter();
		}
	}

	setActiveListOperation( operation: ListOperationsEnum ) {
		setTimeout( () => {
			this.activeListOperation = operation;
		} );
	}

	onOperationSearchEmitter( search: string[], isOperationChange?: boolean ) {
		this.setActiveListOperation( ListOperationsEnum.SEARCH );
		this.search = [ ...search ];

		// reset pagings pageNumber on search change
		this._paging = { ...this.paging, pageNumber: 0, resetPaging: true };

		if ( isOperationChange ) {
			this.onOperationEmitter();
		} else {
			this.searchIsInitialized = true;
			this.onListOperatorInitializationFinish();
		}
	}

	onOperationSortEmitter( sort: string, isOperationChange?: boolean ) {
		this.setActiveListOperation( ListOperationsEnum.SORT );
		this.sort = sort;

		if ( isOperationChange ) {
			this.onOperationEmitter();
		} else {
			this.sortIsInitialized = true;
			this.onListOperatorInitializationFinish();
		}
	}

	onOperationFilterEmitter( activeFilters: IActiveFilters, isOperationChange?: boolean ) {
		this.setActiveListOperation( ListOperationsEnum.FILTER );
		this.activeFilters = { ...activeFilters };

		// reset pagings pageNumber on filter change
		this._paging = { ...this.paging, pageNumber: 0, resetPaging: true };

		if ( isOperationChange ) {
			this.onOperationEmitter();
		} else {
			this.filterIsInitialized = true;
			this.onListOperatorInitializationFinish();
		}
	}

	onOperationPaginationEmitter( paging: IPaging, isOperationChange?: boolean ) {
		this.setActiveListOperation( ListOperationsEnum.PAGINATION );

		const timeout = setTimeout( () => {
			clearTimeout( timeout );
			this._paging = { ...paging };

			if ( isOperationChange ) {
				this.onOperationEmitter();
			} else {
				this.pagingIsInitialized = true;
				this.onListOperatorInitializationFinish();
			}
		}, 0 );
	}
}
